import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeContext from '../../../ThemeContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../../services/api';
import ReportPieChart from './ReportPieChart';


const CommissionReports = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Role & base path detection
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const role = (userInfo.role || 'reseller').toLowerCase();

  const navItems = {
    superadmin: ['resellers', 'clients', 'reports', 'notify', 'admins', 'account'],
    admin:      ['resellers', 'clients', 'reports', 'account'],
    reseller:   ['clients', 'reports', 'account'],
  };

  const tabs = navItems[role] || navItems.reseller;

  const basePath = role === 'superadmin'
    ? '/superadmin'
    : role === 'admin'
      ? '/admin'
      : `/${userInfo.username || 'reseller'}`;

  // Typography
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);

  const clientsDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);

  // Filters from Reports page (if passed)
  const incomingFilters = location.state?.filters || {};
  const selectedResellerIds = (location.state?.selectedResellerIds || []).map(String);


  const [reportView, setReportView] = useState("report");
  // Commission data
  const [commissionRows, setCommissionRows] = useState([]);
  const [loadingCommission, setLoadingCommission] = useState(false);

const params = new URLSearchParams(location.search);

  const isActive = (tab) => location.pathname.includes(`/${tab}`);

  const handleTabClick = (tab) => {
    if (tab === 'clients') {
      navigate(`${basePath}/clients`);
    } else if (tab === 'reports') {
      navigate(`${basePath}/reports?sub=commission`);
    } else {
      navigate(`${basePath}/${tab}`);
    }
  };

  const handleClientSubTab = (subTab) => {
    const path = subTab === 'enterprise' ? 'enterprise-clients' : 'clients';
    navigate(`${basePath}/${path}`);
    setIsClientsDropdownOpen(false);
  };

  const handleReportSubTab = (subTabKey) => {
    navigate(`${basePath}/reports?sub=${subTabKey}`);
    setIsReportsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientsDropdownRef.current && !clientsDropdownRef.current.contains(e.target)) {
        setIsClientsDropdownOpen(false);
      }
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(e.target)) {
        setIsReportsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const parseDateSafely = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = parseDateSafely(value);
    if (!d) return String(value);
    return d.toLocaleDateString();
  };

  // Fetch first session's introducing_party
 
const delay = (ms) => new Promise(res => setTimeout(res, ms));

const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

  const fetchSessionsInBatches = async (caseIds = []) => {
  const sessionsByCaseId = {};
  const chunks = chunkArray(caseIds, 3); // 👈 reduce concurrency (3 is safer than 5)

  for (let group of chunks) {
    await Promise.all(
      group.map(async (caseId) => {
        try {
          // 👇 call ONLY ONE endpoint (same as your working logic)
          const res = await api.get(`/cases/${caseId}/deposition-sessions`);

          sessionsByCaseId[caseId] =
            res.data?.[0]?.introducing_party || null;
        } catch (err) {
          sessionsByCaseId[caseId] = null;
        }
      })
    );

    await delay(300); // 👈 throttle between batches
  }

  return sessionsByCaseId;
};

  useEffect(() => {
    const fetchCommissionData = async () => {
      setLoadingCommission(true);
      try {
        const [resellersResp, clientAdminsResp, casesResp, usersResp] = await Promise.all([
          api.get('/resellers'),
          api.get('/client-admins'),
          api.get('/cases'),
          api.get('/users'),
        ]);

        const resellers = resellersResp.data?.data || resellersResp.data || [];
        const rawClientAdmins = clientAdminsResp.data?.data || clientAdminsResp.data || [];
        const cases = casesResp.data?.data || casesResp.data || [];
        const users = usersResp.data?.users || [];

        const resellerById = {};
        resellers.forEach(r => {
          const id = r.id ?? r.reseller_id ?? r._id;
          if (id != null) resellerById[String(id)] = r;
        });

        const usersById = {};
        const usersByEmail = {};
        users.forEach(u => {
          if (u.id != null) usersById[String(u.id)] = u;
          const email = (u.email || '').toLowerCase();
          if (email) usersByEmail[email] = u;
        });

        const clientAdminByEmail = {};
        const clientAdminById = {};
        rawClientAdmins.forEach(c => {
          if (c.id != null) clientAdminById[String(c.id)] = c;
          const emails = [
            (c.primary_email || '').toLowerCase(),
            (c.email || '').toLowerCase(),
            (c.user?.email || '').toLowerCase(),
          ];
          emails.forEach(e => e && (clientAdminByEmail[e] = c));
        });

        // For reseller: only show their own clients
        let allowedClientAdminIds = new Set();

        if (role === 'reseller') {
          const myResellerId = userInfo.id || userInfo.reseller_id || userInfo._id;
          rawClientAdmins
            .filter(c => {
              const creator = c.reseller_id ?? c.resellerId ?? c.reseller?.id;
              return String(creator) === String(myResellerId);
            })
            .forEach(c => allowedClientAdminIds.add(String(c.id ?? c._id ?? c.client_admin_id)));
        } else {
          // superadmin/admin see reseller-created clients
          rawClientAdmins
            .filter(c => {
              const creatorRole = (c.created_by_role || c.creator_role || '').toLowerCase();
              return creatorRole === 'reseller' || !creatorRole;
            })
            .forEach(c => allowedClientAdminIds.add(String(c.id ?? c._id ?? c.client_admin_id)));
        }

        // Optional: filter by selected resellers (admin/superadmin only)
        if (selectedResellerIds.length > 0 && role !== 'reseller') {
          const filtered = rawClientAdmins.filter(c => {
            const rid = c.reseller_id ?? c.resellerId ?? c.reseller?.id;
            return rid != null && selectedResellerIds.includes(String(rid));
          });
          filtered.forEach(c => allowedClientAdminIds.add(String(c.id ?? c._id ?? c.client_admin_id)));
        }

        let basicRows = cases.map(cs => {
          const clientUser = cs.client || cs.client_user || usersById[String(cs.client_id ?? cs.clientId ?? '')] || null;
          const clientEmail = (clientUser?.email || cs.email || '').toLowerCase();

          const clientAdmin =
            (clientEmail && clientAdminByEmail[clientEmail]) ||
            (cs.client_admin_id != null ? clientAdminById[String(cs.client_admin_id)] : null);

          const clientAdminId = clientAdmin ? String(clientAdmin.id ?? clientAdmin._id ?? clientAdmin.client_admin_id) : null;

          if (!clientAdmin || !allowedClientAdminIds.has(clientAdminId)) {
            return null;
          }

          const resellerIdValue = clientAdmin.reseller_id ?? clientAdmin.resellerId ?? clientAdmin.reseller?.id ?? null;
          const reseller = (resellerIdValue != null && resellerById[String(resellerIdValue)]) || clientAdmin.reseller || {};

          return {
            id: cs.id,
            resellerId: resellerIdValue ? String(resellerIdValue) : null,
            resellerUsername: reseller.username || reseller.primary_email || reseller.reseller_email || '—',
            firmName: clientAdmin.client_name || clientAdmin.primary_name || clientAdmin.organization || '—',
            introducingParty: '—',
            city: cs.city || clientAdmin.city || reseller.city || '—',
            state: cs.state || clientAdmin.state || reseller.state || '—',
            caseName: cs.case_name || cs.name || `${displayText('Case')} #${cs.id}`,
            createdAt: cs.created_at || cs.createdAt || null,
            deletedAt: cs.deleted_at || cs.deletedAt || null,
          };
        }).filter(Boolean);

        // Apply date/month filters if present
        const { activeSubTab, monthFilter, yearFilter } = incomingFilters;
        if (activeSubTab === 'commission' && monthFilter && yearFilter && monthFilter !== 'all' && yearFilter !== 'all') {
          const filterMonth = parseInt(monthFilter, 10);
          const filterYear = parseInt(yearFilter, 10);
          basicRows = basicRows.filter(row => {
            if (!row.createdAt) return false;
            const d = new Date(row.createdAt);
            const yr = d.getFullYear();
            const mn = d.getMonth() + 1;
            if (yr < filterYear) return true;
            if (yr > filterYear) return false;
            return mn <= filterMonth;
          });
        }

        if (basicRows.length === 0) {
          setCommissionRows([]);
          setLoadingCommission(false);
          return;
        }

        const neededCaseIds = basicRows.map(row => row.id);
        const sessionsByCaseId = await fetchSessionsInBatches(neededCaseIds, 5);

        const finalRows = basicRows.map(row => ({
          ...row,
          introducingParty: sessionsByCaseId[row.id] || '—',
        }));

        setCommissionRows(finalRows);
      } catch (error) {
        console.error('Failed to fetch commission data:', error);
        toast.error('Failed to load commission report data.');
        setCommissionRows([]);
      } finally {
        setLoadingCommission(false);
      }
    };

    fetchCommissionData();
  }, [selectedResellerIds.join(''), JSON.stringify(incomingFilters), role, userInfo.id]);

  const actionButtonStyle = {
    background: theme.gradients?.secondary || theme.colors.secondary,
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 'bold',
    fontSize: '12px',
    cursor: 'pointer',
    minWidth: '100px',
    height: '32px',
    fontFamily,
  };

  // Header month/year
  let headerDate = new Date();
  const { activeSubTab, monthFilter, yearFilter } = incomingFilters;
  if (activeSubTab === 'commission' && monthFilter && yearFilter && monthFilter !== 'all' && yearFilter !== 'all') {
    const y = parseInt(yearFilter, 10);
    const m = parseInt(monthFilter, 10) - 1;
    if (!Number.isNaN(y) && !Number.isNaN(m)) {
      headerDate = new Date(y, m, 1);
    }
  }
  const monthName = headerDate.toLocaleString('default', { month: 'long' });
  const yearNum = headerDate.getFullYear();

  const handlePrint = () => window.print();

  const handleExport = () => toast.info('Export functionality not implemented yet.');

  const hasSelection = selectedResellerIds.length > 0;

  return (
    <div
      style={{
        fontFamily,
        backgroundColor: theme.colors.bgPrimary,
        minHeight: '100vh',
        paddingBottom: '3rem',
      }}
    >
      {/* TOP NAVIGATION TABS */}
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {tabs.map((tab) => {
          const isActiveTab = isActive(tab);
          const label = tab === 'account'
            ? 'My Account'
            : tab === 'notify'
              ? 'Notify User'
              : tab.charAt(0).toUpperCase() + tab.slice(1);
          const iconClass =
            tab === 'resellers' ? 'briefcase-fill' :
            tab === 'clients' ? 'people-fill' :
            tab === 'reports' ? 'file-earmark-bar-graph-fill' :
            tab === 'notify' ? 'bell-fill' :
            tab === 'admins' ? 'buildings-fill' :
            'person-lines-fill';

          const isClientsTab = tab === 'clients' && role !== 'reseller';
          const isReportsTab = tab === 'reports';

          return (
            <div
              key={tab}
              ref={isClientsTab ? clientsDropdownRef : isReportsTab ? reportsDropdownRef : null}
              onMouseEnter={() => {
                if (isClientsTab) setIsClientsDropdownOpen(true);
                if (isReportsTab) setIsReportsDropdownOpen(true);
              }}
              onMouseLeave={() => {
                if (isClientsTab) setIsClientsDropdownOpen(false);
                if (isReportsTab) setIsReportsDropdownOpen(false);
              }}
              style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
            >
              <button
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  background: isActiveTab
                    ? theme.gradients?.primary || theme.colors.primary
                    : theme.gradients?.secondary || theme.colors.secondary,
                  color: '#fff',
                  cursor: isClientsTab || isReportsTab ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontFamily,
                }}
                onClick={() => !(isClientsTab || isReportsTab) && handleTabClick(tab)}
              >
                <i className={`bi bi-${iconClass}`} style={{ fontSize: '1rem' }}></i>
                {label}
              </button>

              {/* Clients Dropdown - only for superadmin/admin */}
              {isClientsTab && isClientsDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30,
                    width: '100%',
                    minWidth: '160px',
                    overflow: 'hidden',
                    fontFamily,
                  }}
                >
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily,
                    }}
                    onClick={() => handleClientSubTab('core')}
                    onMouseOver={e => (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                    onMouseOut={e => (e.target.style.background = 'transparent')}
                  >{displayText(" Court Client ")}</button>
                  <hr
                    style={{
                      margin: 0,
                      borderTop: `1px solid ${theme.colors.borderColor}`,
                    }}
                  />
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily,
                    }}
                    onClick={() => handleClientSubTab('enterprise')}
                    onMouseOver={e => (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                    onMouseOut={e => (e.target.style.background = 'transparent')}
                  >{displayText(" Enterprise Client ")}</button>
                </div>
              )}

              {/* Reports Dropdown */}
              {isReportsTab && isReportsDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                  left: 0,
                  backgroundColor: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '6px',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                  zIndex: 30,
                  width: '100%',
                  minWidth: '180px',
                  overflow: 'hidden',
                  fontFamily
                }}>
                  {[
                    { key: 'user', label: 'User Report' },
                    { key: 'commission', label: 'Commission Report' },
                    { key: 'usage', label: 'Usage Report' }
                  ].map((item, idx, arr) => (
                    <React.Fragment key={item.key}>
                      <button
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          background: item.key === 'commission' ? (theme.gradients?.primary || theme.colors.primary) : 'transparent',
                          color: item.key === 'commission' ? '#fff' : theme.colors.textPrimary,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontFamily
                        }}
                        onClick={() => handleReportSubTab(item.key)}
                        onMouseOver={e => item.key !== 'commission' && (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                        onMouseOut={e => item.key !== 'commission' && (e.target.style.background = 'transparent')}
                      >
                        {item.label}
                      </button>
                      {idx < arr.length - 1 && <hr style={{ margin: 0, borderTop: `1px solid ${theme.colors.borderColor}` }} />}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BREADCRUMB + PRINT/EXPORT */}
      <div style={{
        padding: '0 0.75rem',
        marginBottom: '0.75rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <span
            style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 500, color: theme.colors.primary }}
            onClick={() => navigate(`${basePath}/reports?sub=commission`)}
          >
            Commission Report
          </span>
          <span style={{ margin: '0 4px' }}>/</span>
          <span style={{ color: theme.colors.textPrimary }}>Report</span>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
  <button type="button" onClick={handlePrint} style={actionButtonStyle}>
    Print
  </button>

  <button type="button" onClick={handleExport} style={actionButtonStyle}>
    Export
  </button>

  {/* NEW DROPDOWN */}
<select
  value={reportView}
  onChange={(e) => setReportView(e.target.value)}
>
    <option value="report">Report View</option>
    <option value="pie-chart">Pie Chart</option>
  </select>
</div>
      </div>

      {/* WHITE CARD CONTAINER */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '1rem',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        margin: '0 0.75rem 20px',
        fontFamily,
      }}>
        {/* Header Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          borderRadius: '10px',
          backgroundColor: '#ffffff',
          border: `1px solid ${theme.colors.borderColor}`,
          marginBottom: '1rem',
        }}>
          <span style={{ fontWeight: 'bold', fontSize: '13px' }}>Commission Report</span>
          <span style={{ fontSize: '13px' }}>{monthName} {yearNum}</span>
        </div>

        {/* Data Content */}
        {/* Data Content */}

{reportView === "pie-chart" ? (
  <ReportPieChart
    reportType="commission"
    rawData={commissionRows}
    isLoading={loadingCommission}
    defaultViewMode="pie-chart"
  />
) : loadingCommission ? (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
    Loading commission data...
  </div>
) : commissionRows.length === 0 ? (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
    {hasSelection ? (
      <>
        <h4 style={{ marginBottom: '0.5rem' }}>No report data available</h4>
        <p>{displayText("No clients created by selected resellers found for this period.")}</p>
      </>
    ) : (
      'No commission records to show.'
    )}
  </div>
) : (
  commissionRows.map((row, index) => (
            <div
              key={row.id || index}
              style={{
                marginBottom: '0.75rem',
                borderRadius: '6px',
                border: `1px solid ${theme.colors.borderColor}`,
                backgroundColor: '#ffffff',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: theme.colors.primary, color: 'white' }}>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderRight: '1px solid rgba(255,255,255,0.25)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 'bold' }}>{displayText("Reseller")}</div>
                      <div style={{ fontWeight: 'normal', fontSize: '11px', opacity: 0.9, marginTop: '2px' }}>Firm</div>
                    </th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderRight: '1px solid rgba(255,255,255,0.25)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 'bold' }}>Name</div>
                      <div style={{ fontWeight: 'normal', fontSize: '11px', opacity: 0.9, marginTop: '2px' }}>Username</div>
                    </th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderRight: '1px solid rgba(255,255,255,0.25)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 'bold' }}>City</div>
                      <div style={{ fontWeight: 'normal', fontSize: '11px', opacity: 0.9, marginTop: '2px' }}>State</div>
                    </th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderRight: '1px solid rgba(255,255,255,0.25)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 'bold' }}>{displayText("Case")}</div>
                    </th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 'bold' }}>Created</div>
                      <div style={{ fontWeight: 'normal', fontSize: '11px', opacity: 0.9, marginTop: '2px' }}>Deleted</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: '#fff' }}>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.resellerUsername || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.introducingParty || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.city || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.caseName || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{formatDate(row.createdAt)}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#fff', borderTop: `1px solid ${theme.colors.borderColor}` }}>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.firmName || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.clientEmail || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.state || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}></td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{row.deletedAt ? formatDate(row.deletedAt) : '–'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommissionReports;